import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useRequests } from '../../requests/useRequests'
import { PageBanner } from '../Banners/PageBanner'
import { Designer } from '../../types/designer'
import Header from '../Header/Header'
import Footer from '../Footer/Footer'
import ButtonLink from '../Buttons/ButtonLink'
const DesignersList = () => {
  const requests = useRequests()
  const navigate = useNavigate()
  const location = useLocation()
  const [designers, setDesigners] = useState<Designer[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Stany dla paginacji
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [itemsPerPage, setItemsPerPage] = useState<number>(12)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [totalCount, setTotalCount] = useState<number>(0)

  // Pobierz typ z URL query parametrów
  const queryParams = new URLSearchParams(location.search)
  const typeFromUrl = queryParams.get('type')

  useEffect(() => {
    const fetchDesigners = async () => {
      try {
        setLoading(true)

        // Pobierz projektantów z paginacją
        const response = await requests.getDesigners({
          page: currentPage,
          limit: itemsPerPage,
          type: typeFromUrl || undefined
        })

        setDesigners(response.data.data)
        setTotalCount(response.data.meta.total)
        setTotalPages(response.data.meta.pages)
      } catch (err) {
        setError('Wystąpił błąd podczas pobierania danych. Spróbuj ponownie.')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    fetchDesigners()
  }, [requests, currentPage, itemsPerPage, typeFromUrl])

  const handleDesignerClick = (id: number) => {
    navigate(`/designers/${id}`)
  }

  // Obsługa paginacji
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo(0, 0) // Przewiń do góry przy zmianie strony
  }

  if (error) {
    return (
      <div className='text-center pt-100 pb-100'>
        <div className='alert alert-danger'>
          <p>{error}</p>
          <button className='btn btn__primary mt-20' onClick={() => window.location.reload()}>
            Spróbuj ponownie
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Header />
      <PageBanner title='Projektanci' description='Odkryj talenty najlepszych projektantów i nawiąż z nimi współpracę' />

      <section className='designers-grid tw-py-24 md:tw-py-28 lg:tw-py-36'>
        <div className='container'>

          {loading ? (
            <div className='text-center'>
              <div className='spinner-border text-primary' role='status'>
                <span className='sr-only'>Ładowanie...</span>
              </div>
            </div>
          ) : designers.length > 0 ? (
            <>
              <div className='row'>
                {designers.map(designer => (
                  <div key={designer.id} className='col-sm-12 col-md-6 col-lg-3'>
                    <div className='designer-card' onClick={() => handleDesignerClick(designer.id)}>
                      <div className='designer-card__img'>
                        <img
                          src={designer.imagePresignedUrl || designer.image || '/assets/images/designers/placeholder.jpg'}
                          alt={designer.name}
                        />
                        <div className='designer-card__overlay'>
                          <div className='designer-card__content'>
                            <h5 className='designer-card__title'>{designer.name}</h5>
                            <p className='designer-card__desc'>{designer.shortDescription}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Paginacja - ZAWSZE WIDOCZNA */}
              {designers.length > 0 && (
                <div className='pagination-section mt-50 mb-30'>
                  <div className='row align-items-center'>
                    <div className='col-12'>
                      <div className='pagination-info-wrapper text-center mb-4'>
                        <div className='pagination-current-page'>
                          <span className='badge badge-pill badge-primary px-4 py-2 current-page-large'>
                            <span className='page-indicator'>Strona</span> <strong className='page-number'>{currentPage}</strong> <span className='page-indicator'>z</span> <strong className='page-number'>{totalPages}</strong>
                          </span>
                        </div>
                        <div className='pagination-total-info mt-2'>
                          Wyświetlanie <strong>{designers.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</strong> - <strong>{Math.min(currentPage * itemsPerPage, totalCount)}</strong> z <strong>{totalCount}</strong> projektantów
                        </div>
                      </div>

                      {totalPages > 1 && (
                        <nav aria-label='Nawigacja strony projektantów' className='product-pagination'>
                          <ul className='pagination pagination-lg justify-content-center'>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <a
                                className='page-link'
                                href='#'
                                onClick={(e) => {
                                  e.preventDefault()
                                  if (currentPage > 1) handlePageChange(currentPage - 1)
                                }}
                                aria-label='Poprzednia'
                              >
                                <span aria-hidden='true'>&laquo;</span>
                                <span className='sr-only'>Poprzednia</span>
                              </a>
                            </li>

                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page =>
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 2 && page <= currentPage + 2) // Pokaż więcej stron wokół aktualnej
                              )
                              .map((page, index, array) => {
                                const showEllipsisBefore = index > 0 && array[index - 1] !== page - 1
                                const showEllipsisAfter = index < array.length - 1 && array[index + 1] !== page + 1

                                return (
                                  <React.Fragment key={page}>
                                    {showEllipsisBefore && (
                                      <li className='page-item disabled'>
                                        <span className='page-link'>...</span>
                                      </li>
                                    )}

                                    <li className={`page-item ${page === currentPage ? 'active' : ''}`}>
                                      <a
                                        className='page-link'
                                        href='#'
                                        onClick={(e) => {
                                          e.preventDefault()
                                          handlePageChange(page)
                                        }}
                                      >
                                        {page}
                                      </a>
                                    </li>

                                    {showEllipsisAfter && (
                                      <li className='page-item disabled'>
                                        <span className='page-link'>...</span>
                                      </li>
                                    )}
                                  </React.Fragment>
                                )
                              })}

                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <a
                                className='page-link'
                                href='#'
                                onClick={(e) => {
                                  e.preventDefault()
                                  if (currentPage < totalPages) handlePageChange(currentPage + 1)
                                }}
                                aria-label='Następna'
                              >
                                <span aria-hidden='true'>&raquo;</span>
                                <span className='sr-only'>Następna</span>
                              </a>
                            </li>
                          </ul>
                        </nav>
                      )}

                      <div className='items-per-page-selector-wrapper text-center mt-4'>
                        <div className='items-per-page-selector d-inline-block'>
                          <label className='mr-2'>Projektantów na stronie:</label>
                          <select
                            className='form-control form-control-sm d-inline-block w-auto'
                            value={itemsPerPage}
                            onChange={(e) => {
                              const newItemsPerPage = parseInt(e.target.value, 10)
                              setItemsPerPage(newItemsPerPage)
                              setCurrentPage(1)
                            }}
                          >
                            <option value='12'>12</option>
                            <option value='24'>24</option>
                            <option value='48'>48</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className='text-center'>
              <p className='tw-mb-4'>Brak projektantów do wyświetlenia.</p>
              <ButtonLink
                to='/designers'
                onClick={() => navigate('/designers')}
              >
                Zobacz wszystkich projektantów
              </ButtonLink>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  )
}

export default DesignersList
